import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { log } from '@/logger/logger.ts';
import { useGlobalStore } from '@/state/global.state';
import { I18N_KEYS } from '@/language/keys';
import type { SnackbarData } from '@/state/global.state';

const logger = log.getLogger('useLinkedFigmaAccount');

export type FigmaConnection = { connected: true; figmaUserId: string; figmaEmail?: string } | { connected: false };

export const FIGMA_CONNECTION_QUERY_KEY = 'figmaConnection';

/**
 * A Figma link only changes through an explicit link/unlink — both of which write this cache — or
 * through the grant being revoked at Figma, which surfaces on the next proxied call. Without a
 * staleTime every mount refetches, and the Figma node footer mounts one per node.
 */
const FIGMA_CONNECTION_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * Pass `includeEmail` only where the email is displayed: it costs a Figma userinfo round trip on the
 * server. It is part of the query key, so the cheap link-status callers never wait on it.
 */
export const useLinkedFigmaAccount = ({ includeEmail = false }: { includeEmail?: boolean } = {}) =>
  useQuery({
    queryKey: [FIGMA_CONNECTION_QUERY_KEY, { includeEmail }],
    queryFn: async (): Promise<FigmaConnection> => {
      // Capped rather than off: a 5xx on a route every editor mount asks about tops out at 4 calls
      // instead of 8 (3 axios-retry x TanStack's 1 retry), and a genuine network blip still gets a
      // retry beneath TanStack's.
      const { data } = await safeAxios.get<FigmaConnection>(API_ROUTES.auth.figmaConnections(), {
        params: includeEmail ? { includeEmail: true } : undefined,
        'axios-retry': { retries: 1 },
      });
      return data;
    },
    staleTime: FIGMA_CONNECTION_STALE_TIME_MS,
  });

const connectFigmaAccount = async (params: { code: string; redirectUri: string }): Promise<FigmaConnection> => {
  const { data } = await safeAxios.post<FigmaConnection>(API_ROUTES.auth.figmaConnect(), params);
  return data;
};

type ConnectFigmaAccountMutationOptions = {
  successSnackbar?: Partial<SnackbarData>;
};

export const useConnectFigmaAccountMutation = (options?: ConnectFigmaAccountMutationOptions) => {
  const queryClient = useQueryClient();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const { t } = useTranslation();
  const successSnackbar = options?.successSnackbar;

  return useMutation({
    mutationFn: connectFigmaAccount,
    onSuccess: (connection) => {
      // Linking is already confirmed, so expose it synchronously. Waiting for invalidation to refetch
      // leaves a false "disconnected" frame that can cancel flows waiting to continue after OAuth.
      queryClient.setQueriesData<FigmaConnection>({ queryKey: [FIGMA_CONNECTION_QUERY_KEY] }, (previous) =>
        previous?.connected && previous.figmaEmail ? { ...connection, figmaEmail: previous.figmaEmail } : connection,
      );
      // Refresh the email-bearing variant because the connect response intentionally has no email.
      void queryClient.invalidateQueries({ queryKey: [FIGMA_CONNECTION_QUERY_KEY] });
      updateSnackbarData({
        text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.SNACKBAR.LINK_SUCCESS),
        isOpen: true,
        ...successSnackbar,
      });
    },
    onError: (error) => {
      logger.error('Failed to connect Figma:', error);
      updateSnackbarData({
        text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.SNACKBAR.LINK_FAILURE),
        isOpen: true,
      });
    },
  });
};
